/**
 * The control room's read model: one row per CALENDAR — that is, one month of
 * the picked brand's plan.
 *
 * Brand-scoped, like every other surface in the shell. The brand is chosen in
 * the nav rail and `GET /content-calendar/:brandId/plans` already returns that
 * brand's months, so this page reads what exists rather than asking for a
 * cross-brand roll-up the domain model does not have.
 */
import type {
  BrandCalendarSettings,
  CalendarMonthPlan,
  CalendarMonthPlanListRow,
  CalendarPlanSlot,
  CalendarSlotCounts,
} from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { monthPulseCounts } from "../slotStage";

/**
 * The five buckets the header counts and the filters cut by.
 *
 * Every one is read from the plan's OWN status. The earlier set had an
 * "In review" bucket taken from `slots.some(s => s.status === IN_CLIENT_REVIEW)`
 * — a question this page cannot ask, because the list endpoint returns no
 * slots. Worse, it was the wrong unit: several review batches can be open at
 * once with disjoint membership, so "with the client" is a fact about a post
 * and never about a month.
 *
 * No bucket is a month waiting on a person anymore: the month-level decision
 * there used to be — approving the skeleton — is gone, and everything a person
 * still owes the month is owed POST by post.
 */
export type CalendarState =
  | "not_planned"
  | "failed"
  | "planning"
  | "running"
  | "delivered";

export const STATE_LABEL: Record<CalendarState, string> = {
  not_planned: "Not planned",
  failed: "Planning failed",
  planning: "Planning",
  running: "Running",
  delivered: "Delivered",
};

export const STATE_ORDER: CalendarState[] = [
  "failed",
  "planning",
  "running",
  "not_planned",
  "delivered",
];

/**
 * One month of this brand's calendar.
 *
 * Shaped by what `GET /content-calendar/:brandId/plans` actually returns: every
 * plan scalar plus grouped per-status slot counts, but no full slots. The
 * compact map is enough for progress and ownership; media mix and stale-run
 * detection still require a fully loaded month.
 */
export interface CalendarRow {
  planId: string | null;
  year: number;
  /** 1-12. */
  month: number;
  /** Raw plan status; null when the month has no plan row at all. */
  planStatus: string | null;
  /**
   * The format mix this month was CLAIMED for, snapshotted onto the plan when
   * it was generated. Null on a month with no plan row.
   *
   * Deliberately not re-read from the brand's settings: the schema snapshots
   * these precisely so a later cadence change cannot move the target a month
   * was held to. Reading settings here would make every past month's scope
   * line start lying the day someone edits the cadence.
   */
  targets: { static: number; vertical: number; horizontal: number } | null;
  /** Last planning failure, still on the row. The month sits at DRAFT. */
  generationError: string | null;
  approvedAt: string | null;
  /** Absent on a row read from the list endpoint, which returns no slots. */
  slots?: CalendarPlanSlot[];
  /** Always present on planned rows read from the list endpoint. */
  slotCounts: CalendarSlotCounts | null;
  briefBySlot?: Map<string, BriefRailInfo>;
  /** Most recent write anywhere in the month, for the last-action line. */
  lastActionAt: string | null;
  /** Plain sentence for the last thing that happened. Null when nothing has. */
  lastAction: string | null;
  /** Who signed the month off, when it is signed. */
  signedBy: string | null;
}

/**
 * Total over the live plan statuses. The nine legacy values (PENDING_APPROVAL,
 * IN_CLIENT_REVIEW, BRIEFS_DRAFT, MONTH_SIGNED_OFF and five more) all read as
 * APPROVED on the server, so they land in "running" here through the same
 * fall-through rather than each needing a case.
 */
export function calendarState(row: CalendarRow): CalendarState {
  const status = row.planStatus;
  // A month that TRIED and fell over is not a month nobody has touched. Both
  // sit at DRAFT, and only `generationError` tells them apart — so lumping them
  // under one tag puts "here is what it would plan" and "it broke, start it
  // again" behind the same word and the same filter.
  if (!status || status === "DRAFT") {
    return row.generationError ? "failed" : "not_planned";
  }
  if (status === "GENERATING") return "planning";
  if (status === "ARCHIVED") return "delivered";
  return "running";
}

/** How far through the month the work is, and what is left. */
export interface Progress {
  total: number;
  ready: number;
  needsYou: number;
  /** Posts an agent is working on RIGHT NOW. Stranded runs are not counted. */
  working: number;
  /** Posts currently with the client; deliberately separate from team work. */
  withClient: number;
  /** Past-due posts kept out of the active attention count. */
  missed: number;
  /** 0-1, for the one bar on the row. */
  fraction: number;
}

/**
 * `now` is what separates "an agent is working" from "an agent stopped
 * responding an hour ago". Both sit in GENERATING_IDEAS; only the clock tells
 * them apart, and `slotTone` moves the stranded one to the human tone past the
 * 45-minute sweep. A live badge on a dead run is the worst kind of lie a status
 * can tell, so the count is never taken without it.
 */
export function progressOf(row: CalendarRow, now?: number): Progress | null {
  if (!row.slots && !row.slotCounts) return null;
  if (!row.slots && row.slotCounts) {
    const c = row.slotCounts;
    const total = Object.values(c).reduce((sum, value) => sum + value, 0);
    const ready = c.SIGNED_OFF + c.CLIENT_APPROVED;
    return {
      total,
      ready,
      needsYou: c.IDEATION,
      working: c.GENERATING_IDEAS,
      withClient: c.IN_CLIENT_REVIEW,
      missed: c.MISSED,
      fraction: total === 0 ? 0 : ready / total,
    };
  }
  const c = monthPulseCounts(row.slots ?? [], row.briefBySlot, now);
  const rawClient = row.slots?.filter((slot) => slot.status === "IN_CLIENT_REVIEW").length ?? 0;
  return {
    total: c.total,
    ready: c.ready,
    needsYou: c.human,
    working: c.machine,
    withClient: rawClient,
    missed: c.missed,
    fraction: c.total === 0 ? 0 : c.ready / c.total,
  };
}

/**
 * The one thing this month wants from a person right now, or null.
 *
 * Planning failures come from the plan row. Post-level attention comes from the
 * grouped status map, with client review deliberately excluded because nothing
 * the internal team does advances it.
 */
export function needsYouLine(row: CalendarRow): string | null {
  if (row.generationError && row.planStatus === "DRAFT") {
    // The state tag already says it failed. Repeating that here and again in
    // the last-action line says the same thing three times; this says only the
    // part the tag cannot, which is what to do about it.
    return "Start it again.";
  }
  const progress = progressOf(row);
  if (!progress || progress.needsYou === 0) return null;
  return progress.needsYou === 1
    ? "1 post is waiting on you"
    : `${progress.needsYou} posts are waiting on you`;
}

export interface ScopeLine {
  label: string;
  /** How many are placed. Null when the row carries no slots. */
  actual: number | null;
  target: number;
}

/**
 * The format mix this month is held to.
 *
 * A PLANNED month reads its own snapshot. A month with no plan row has no
 * snapshot to read, so it falls back to the brand's current cadence — which is
 * correct there and only there: that IS the mix it would be planned for if you
 * started it now, which is the whole claim the proposal row makes.
 *
 * `actual` is null wherever the row has no slots, and the row prints the target
 * alone rather than inventing a numerator. The three buckets are the contract:
 * there is no fourth total on the server, the sum IS the total, and a bucket
 * set to 0 survives proration untouched because "no horizontal video" is an
 * instruction rather than a rounding hint.
 */
export function scopeLines(
  row: CalendarRow,
  settings: BrandCalendarSettings | null,
): ScopeLine[] | null {
  const target = row.targets
    ? row.targets
    : settings
      ? {
          static: settings.staticPostsPerMonth,
          vertical: settings.verticalVideoPostsPerMonth,
          horizontal: settings.horizontalVideoPostsPerMonth,
        }
      : null;
  if (!target) return null;

  const live = row.slots?.filter((s) => s.status !== "MISSED");
  const statics = live?.filter((s) => s.mediaType === "STATIC").length ?? null;
  const horizontal =
    live?.filter((s) => s.mediaType !== "STATIC" && s.orientation === "HORIZONTAL")
      .length ?? null;

  return [
    { label: "static", actual: statics, target: target.static },
    {
      label: "video · 9:16 / 4:5",
      actual:
        live && statics !== null && horizontal !== null
          ? live.length - statics - horizontal
          : null,
      target: target.vertical,
    },
    { label: "video · 16:9", actual: horizontal, target: target.horizontal },
  ];
}

/** Where a month opens. One definition, shared by the index and its grid. */
export function monthPath(row: { year: number; month: number }): string {
  return `/dashboard/content-calendar?year=${row.year}&month=${row.month}`;
}

/** Newest first. The month in flight sits at the top because it is the newest. */
export function byNewest(a: CalendarRow, b: CalendarRow): number {
  return b.year - a.year || b.month - a.month;
}


/**
 * What the next month would be built from, if you asked for it now.
 *
 * NO BLANK CANVAS: a month with no plan does not get an empty state and a
 * button. It states what it would produce and what it would read, so the
 * decision to start it is made against something rather than against a cursor.
 *
 * Every clause here is sourced from `planContextBuilder`, which is the one
 * place that says what the planner is HANDED as opposed to what it may go
 * looking for: the slot count, the exact format quotas, the pillar ids it must
 * choose from, the neighbouring titles it must not repeat, and the brand's
 * standing preferences. That list is the whole of it.
 *
 * Deliberately absent: signals, trends, brand DNA and the strategy document.
 * The same file states that those "arrive via the toolbelt", so the planner MAY
 * read them and may not. "Built from 6 accepted signals" would be a number this
 * page cannot stand behind, and a proposal that overstates its inputs is worse
 * than one that lists fewer.
 */
export interface Proposal {
  /** Total posts, and the exact per-format budget. */
  total: number;
  mix: ScopeLine[];
  /** Pillars the planner must choose from; null until the read lands. */
  pillars: number | null;
  /** Standing preferences injected into the run; null until the read lands. */
  memories: number | null;
  /** The month whose titles it is told not to repeat, if there is one. */
  avoids: { year: number; month: number } | null;
}

/** What the planner would be handed, for the months that have not started. */
export interface ProposalContext {
  pillars: number | null;
  memories: number | null;
  rows: CalendarRow[];
}

export function proposalFor(
  row: CalendarRow,
  settings: BrandCalendarSettings | null,
  context: ProposalContext,
): Proposal | null {
  const mix = scopeLines(row, settings);
  if (!mix) return null;
  const previous = context.rows
    .filter((r) => r.planStatus && (r.year < row.year || (r.year === row.year && r.month < row.month)))
    .sort(byNewest)[0];
  return {
    total: mix.reduce((n, l) => n + l.target, 0),
    mix,
    pillars: context.pillars,
    memories: context.memories,
    avoids: previous ? { year: previous.year, month: previous.month } : null,
  };
}

/* ===========================================================================
   FROM THE API
   =========================================================================== */

function lastActionFor(plan: CalendarMonthPlan): string | null {
  switch (plan.status) {
    case "GENERATING":
      return "Planning started";
    case "DRAFT":
      return plan.generationError ? "Planning failed" : null;
    case "ARCHIVED":
      return "Archived";
    default:
      // `approvedAt` is stamped by the plan run itself now, so it dates the month
      // rather than anyone's decision — which is what the line should say.
      return plan.approvedAt
        ? `Planned ${new Date(plan.approvedAt).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
          })}`
        : "In production";
  }
}

function rowFromPlan(plan: CalendarMonthPlanListRow): CalendarRow {
  return {
    planId: plan.id,
    year: plan.year,
    month: plan.month,
    planStatus: plan.status,
    targets: {
      static: plan.targetStaticCount,
      vertical: plan.targetVerticalVideoCount,
      horizontal: plan.targetHorizontalVideoCount,
    },
    generationError: plan.generationError,
    approvedAt: plan.approvedAt,
    slotCounts: plan.slotCounts,
    lastActionAt: plan.updatedAt,
    lastAction: lastActionFor(plan),
    signedBy: null,
  };
}

/**
 * A month the brand has no plan row for.
 *
 * Exported because the index synthesises one for every empty cell in the year
 * it is showing: the list endpoint only returns months that exist, and the
 * annual grid has twelve cells to fill whatever came back.
 */
export function unplannedRow(year: number, month: number): CalendarRow {
  return {
    planId: null,
    year,
    month,
    planStatus: null,
    targets: null,
    generationError: null,
    approvedAt: null,
    slotCounts: null,
    lastActionAt: null,
    lastAction: null,
    signedBy: null,
  };
}

/**
 * The run of months as the index shows it: every plan row the list endpoint
 * returned, plus a proposal row for each month inside the brand's planning
 * window that has no plan yet. The window starts at the current month and
 * reaches as far ahead as the cadence plans, so the nearest unplanned month is
 * always on the page to be started.
 */
export function rowsFromPlans(
  plans: CalendarMonthPlanListRow[],
  settings: BrandCalendarSettings | null,
  now: Date = new Date(),
): CalendarRow[] {
  const rows = plans.map(rowFromPlan);
  const have = new Set(rows.map((r) => `${r.year}-${r.month}`));
  const ahead = Math.max(1, settings?.planningPeriodMonths ?? 1);
  for (let i = 0; i <= ahead; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1));
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    if (!have.has(`${year}-${month}`)) rows.push(unplannedRow(year, month));
  }
  return rows.sort(byNewest);
}
